import React from 'react'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import Modal from '@app/components/Modal'
import Spacer from '@app/components/atoms/Spacer'
import {
  ContentCard,
  ContentHeading,
  ContentSeparator,
} from '@app/evaluation/components/Preparation.styles'

type SettingType = 'topo_stance' | 'bunk_depth'

interface ConfirmDefaultSettingChangeModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  settingType: SettingType
  newValue: string
}

const getSettingInfo = (settingType: SettingType) => {
  switch (settingType) {
    case 'topo_stance':
      return {
        title: 'Confirm Stance/Lie Default Change',
        settingName: 'Default Stance or Lie in Landing Zone',
        affectedAdjustment: 'Stance or Lie in Landing Zone',
        adjustmentCategory: 'Topography',
      }
    case 'bunk_depth':
      return {
        title: 'Confirm Bunker Depth Default Change',
        settingName: 'Default Bunker Depth',
        affectedAdjustment: 'Bunker Depth',
        adjustmentCategory: 'Bunkers',
      }
    default:
      return {
        title: 'Confirm Default Setting Change',
        settingName: 'Default Setting',
        affectedAdjustment: 'Adjustment values',
        adjustmentCategory: 'Course',
      }
  }
}

const ConfirmDefaultSettingChangeModal: React.FC<
  ConfirmDefaultSettingChangeModalProps
> = ({ isOpen, onClose, onConfirm, settingType, newValue }) => {
  const settingInfo = getSettingInfo(settingType)

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="600px"
      closeOnBackdropClick={true}
      showCloseButton={true}
    >
      <ContentCard
        style={{
          width: '100%',
          maxWidth: '560px',
          position: 'relative',
          boxSizing: 'border-box',
        }}
      >
        <ContentHeading>{settingInfo.title}</ContentHeading>
        <ContentSeparator />
        <div style={{ padding: '16px 0', maxWidth: '100%' }}>
          <p style={{ fontSize: 14, lineHeight: 1.5, wordWrap: 'break-word' }}>
            You are about to change the{' '}
            <strong>{settingInfo.settingName}</strong> to:
          </p>
          <p
            style={{
              fontSize: 16,
              fontWeight: 'bold',
              margin: '16px 0',
              padding: '12px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              textAlign: 'center',
              wordWrap: 'break-word',
            }}
          >
            {newValue}
          </p>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.5,
              color: '#d32f2f',
              wordWrap: 'break-word',
            }}
          >
            <strong>Warning:</strong> This change will affect all{' '}
            <strong>{settingInfo.affectedAdjustment}</strong> values on the{' '}
            <strong>{settingInfo.adjustmentCategory}</strong> adjustment for all
            holes. Existing values may be overwritten.
          </p>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.5,
              marginTop: 12,
              wordWrap: 'break-word',
            }}
          >
            Are you sure you want to continue?
          </p>
        </div>
        <ContentSeparator />
        <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
          <RoundedButton title="Cancel" onPress={onClose} />
          <Spacer width={8} />
          <RoundedButton title="Confirm Change" onPress={onConfirm} />
        </div>
      </ContentCard>
    </Modal>
  )
}

export default ConfirmDefaultSettingChangeModal
export type { SettingType }
